import Button from "@/Components/Button/Button";
import Checkbox from "@/Components/Checkbox/Checkbox";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import useBranchId from "@/shared/hooks/useBranchId";
import usePlaySoundHook from "@/shared/hooks/usePlaySoundHook";
import useSystemTrackerHook from "@/shared/hooks/useSystemTrackerHook";
import useUploadCloudinaryHook from "@/shared/hooks/useUploadCloudinaryHook";
import useUserSettingsHook from "@/shared/hooks/useUserSettingsHook";
import useOrderTrackerIconSignature from "@/shared/mutations/app/useOrderTrackerIconSignature";
import useOrderTrackerIconSignatureUpdate from "@/shared/mutations/app/useOrderTrackerIconSignatureUpdate";
import {
  DEFAULT_ALARM_VOLUME,
  DEFAULT_EXTRA_ALARM_MINUTE_DELAY,
  orderStatusses,
  soundOptions,
} from "@/shared/utils/constant";
import { AlarmFileSounds } from "@/shared/utils/enum";
import { getFormData, getOrderTrackerIconUrl } from "@/shared/utils/helpers";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabPanel } from "react-tabs";
import { useImmer } from "use-immer";
import File from "../Base/File";
import Boolean from "../Boolean/BooleanWithLabel";
import IconToolTip from "../Icon/IconToolTip";
import ListTab, { ListTabItem } from "../List/ListTab";
import NativeNotificationEnabler from "../Native/NativeNotificationEnabler";
import Modal from "./Modal";
import useLayoutData from "@/shared/hooks/useLayoutData";

const options = [
  { label: "infinity", value: 0 },
  { label: "1times", value: 1 },
  { label: "2times", value: 2 },
  { label: "3times", value: 3 },
  { label: "5times", value: 5 },
  { label: "10times", value: 10 },
];

const alarmVolumeOptions = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
];

const extraAlarmMinuteDelayOptions = [
  { label: "off", value: 0 },
  { label: "1minute", value: 1 },
  { label: "3minutes", value: 3 },
  { label: "5minutes", value: 5 },
];

const timeToNextDayOptions = Array.from(Array(24).keys()).map((i) => ({
  label: `0${i}:000`.slice(-6, -1),
  value: `${i}`,
}));

const SystemTrackerModal = ({ onExit, ...props }) => {
  const { t } = useTranslation("", { keyPrefix: "orders_live" });
  const [tab, setTab] = useState(0);

  const tabList = useMemo(
    () => [t("userSpecificSettings"), t("generalSettings")],
    [t]
  );

  return (
    <Modal onExit={onExit} width="75rem" minHeight="auto" {...props}>
      <ListTab>
        {tabList.map((label, index) => (
          <ListTabItem
            key={index}
            onClick={() => setTab(index)}
            active={tab === index}
          >
            {label}
          </ListTabItem>
        ))}
      </ListTab>
      <Tabs selectedIndex={tab}>
        <TabPanel>
          <UserSettings onExit={onExit} {...props} />
        </TabPanel>
        <TabPanel>
          <TrackerSettings onExit={onExit} {...props} />
        </TabPanel>
      </Tabs>
    </Modal>
  );
};

/* ------COMPONENTS-START */

interface UserSettingProps {
  acceptedAutomatically: boolean;
  isPosOrderAcceptedAuto: boolean;
  playSoundCount: any;
  playSound: boolean;
  newOrders: boolean;
  isActive: boolean;
  isOrderConfirmAsapMinButtonActive: boolean;
  alarmVolume: number;
  extraAlarmMinuteDelay: number;
  extraAlarmSoundTimes: number;
  stickyPosOrder: boolean;
  lanes: string[];
  sound: number;
  isPosOrderPrintedAuto: boolean;
  sources: { label: string; value: number[] }[];
  deliveryMethods: { label: string; value: number[] }[];
  showOrderCount: boolean;
}

export type KeyProps = keyof UserSettingProps;

const UserSettings = ({ onExit }) => {
  const { t } = useTranslation("", { keyPrefix: "orders_live" });
  const { t: tCommon } = useTranslation("");
  const {
    query,
    putUserSettings,
    sources: sourceOptions,
    deliveryMethods: deliveryMethodOptions,
  } = useUserSettingsHook();

  const [fields, setFields] = useImmer<UserSettingProps>({
    acceptedAutomatically: null,
    isPosOrderAcceptedAuto: null,
    playSoundCount: null,
    playSound: null,
    newOrders: null,
    isActive: null,
    isOrderConfirmAsapMinButtonActive: null,
    alarmVolume: 1,
    extraAlarmMinuteDelay: 0,
    stickyPosOrder: null,
    lanes: [],
    sound: soundOptions[0].value,
    isPosOrderPrintedAuto: null,
    sources: [],
    deliveryMethods: [],
    extraAlarmSoundTimes: 3,
    showOrderCount: true,
  });
  const { playSound: playMySound, stop } = usePlaySoundHook(fields.alarmVolume);

  useEffect(() => {
    if (query.data) {
      setFields((draft) => {
        draft.acceptedAutomatically =
          query.data.is_order_accepted_automatically;
        draft.isPosOrderAcceptedAuto =
          query.data.is_pos_order_accepted_automatically ?? true;
        draft.playSoundCount = options.find(
          (i) => i.value === query.data?.play_sound_times
        );
        draft.playSound = query.data?.play_sound;
        draft.newOrders = query.data?.show_only_new_orders;
        draft.isActive = query.data?.is_active;
        draft.isOrderConfirmAsapMinButtonActive =
          query.data?.order_confirm_asap_minutes_buttons_is_active;
        draft.alarmVolume = query.data?.alarm_volume || DEFAULT_ALARM_VOLUME;
        draft.extraAlarmMinuteDelay =
          query.data?.extra_alarm_minute_delay ||
          DEFAULT_EXTRA_ALARM_MINUTE_DELAY;
        draft.extraAlarmSoundTimes = query.data?.extra_alarm_sound_times || 3;
        draft.stickyPosOrder = query.data?.sticky_pos_order_screen ?? false;
        draft.lanes =
          query.data?.lanes ?? orderStatusses.map((o) => `${o.value[0]}`);
        draft.sound =
          query.data?.alarm_sound_file_choice ?? AlarmFileSounds.DOOR_BELL;
        draft.isPosOrderPrintedAuto =
          query.data?.is_pos_order_printed_automatically ?? true;
        draft.sources = sourceOptions.filter((d) =>
          d.value.some((v) => query.data?.sources.includes(v))
        );
        draft.deliveryMethods = deliveryMethodOptions.filter((d) =>
          d.value.some((v) => query.data?.delivery_methods.includes(v))
        );
        draft.showOrderCount = query.data?.show_order_count ?? true;
      });
    }
  }, [query.data]);

  useEffect(() => {
    if (fields.acceptedAutomatically && fields.playSoundCount.value === 0) {
      setFields((draft) => {
        draft.playSoundCount = options[4];
      });
    }
  }, [fields.acceptedAutomatically, fields.playSoundCount]);

  const onSubmitForm = (e) => {
    e.preventDefault();
    const submitData = {
      is_order_accepted_automatically: fields.acceptedAutomatically,
      is_pos_order_accepted_automatically: fields.isPosOrderAcceptedAuto,
      is_pos_order_printed_automatically: fields.isPosOrderAcceptedAuto
        ? fields.isPosOrderPrintedAuto
        : false,
      play_sound: fields.playSound,
      play_sound_times: fields.playSoundCount?.value,
      show_only_new_orders: fields.newOrders,
      is_active: fields.isActive,
      extra_alarm_minute_delay: fields.extraAlarmMinuteDelay,
      extra_alarm_sound_times: fields.extraAlarmSoundTimes,
      alarm_volume: fields.alarmVolume,
      order_confirm_asap_minutes_buttons_is_active:
        fields.isOrderConfirmAsapMinButtonActive ?? false,
      sticky_pos_order_screen: fields.stickyPosOrder,
      lanes: fields.lanes,
      alarm_sound_file_choice: fields.sound,
      sources: fields.sources.map((s) => s.value).flat(),
      delivery_methods: fields.deliveryMethods.map((d) => d.value).flat(),
      show_order_count: fields.showOrderCount,
    };
    putUserSettings.mutateAsync(submitData, {
      onSuccess: () => onExit(),
    });
  };

  useEffect(() => {
    playMySound(fields.sound);
  }, [fields.sound]);

  const setSingleField = (desiredField: KeyProps, value: any) => {
    setFields((draft) => {
      //@ts-ignore
      draft[desiredField] = value;
    });
  };

  return (
    <div>
      {query.isPending && <Spinner />}
      {query.isError && <div>error</div>}
      {query.isSuccess && (
        <form onSubmit={onSubmitForm}>
          <div className="flex flex-col justify-between gap-8">
            <Boolean
              onChange={(e) => setSingleField("isActive", e.target.checked)}
              checked={fields.isActive}
            >
              {t("active")}
            </Boolean>

            <Wrapper title={t("alarmSound")}>
              <div className="flex" style={{ width: "200px" }}>
                <div className="w-full">
                  <Select
                    options={soundOptions}
                    onChange={(val) => {
                      stop(fields.sound);
                      setSingleField("sound", val.value);
                    }}
                    value={soundOptions.find((o) => o.value === fields.sound)}
                    getOptionLabel={(option) => tCommon(option.label)}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.getElementById("root")}
                  />
                </div>
                <div
                  onClick={() => {
                    stop(fields.sound);
                    playMySound(fields.sound);
                  }}
                >
                  <IconToolTip text="play" variants="soundup" />
                </div>
              </div>
            </Wrapper>

            <Wrapper>
              <Boolean
                onChange={(e) => setSingleField("playSound", e.target.checked)}
                checked={fields.playSound}
              >
                {t("playSound")}
              </Boolean>
              <div className="w-50">
                {fields.playSound && (
                  <Select
                    options={
                      fields.acceptedAutomatically ? options.slice(1) : options
                    }
                    onChange={(val) => setSingleField("playSoundCount", val)}
                    value={fields.playSoundCount}
                    getOptionLabel={(option) => `${t(`${option.label}`)}`}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.getElementById("root")}
                  />
                )}
              </div>
            </Wrapper>

            {fields.playSound && (
              <>
                <Wrapper title={t("alarmVolume")}>
                  <div className="w-50">
                    <Select
                      options={alarmVolumeOptions}
                      onChange={(val) =>
                        setSingleField("alarmVolume", val.value)
                      }
                      value={alarmVolumeOptions.find(
                        (o) => o.value === fields.alarmVolume
                      )}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.getElementById("root")}
                    />
                  </div>
                </Wrapper>

                <Wrapper title={t("extraAlarm")}>
                  <div className="w-50">
                    <Select
                      options={extraAlarmMinuteDelayOptions}
                      onChange={(val) =>
                        setSingleField("extraAlarmMinuteDelay", val.value)
                      }
                      value={extraAlarmMinuteDelayOptions.find(
                        (o) => o.value === fields.extraAlarmMinuteDelay
                      )}
                      getOptionLabel={(option) => `${t(`${option.label}`)}`}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.getElementById("root")}
                    />
                  </div>
                </Wrapper>

                <Wrapper title={t("extraAlarmCount")}>
                  <div className="w-50">
                    <Select
                      options={options.slice(1)}
                      onChange={(val) =>
                        setSingleField("extraAlarmSoundTimes", val.value)
                      }
                      value={options.find(
                        (o) => o.value === fields.extraAlarmSoundTimes
                      )}
                      getOptionLabel={(option) => `${t(`${option.label}`)}`}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.getElementById("root")}
                    />
                  </div>
                </Wrapper>
              </>
            )}

            <Wrapper title={t("desiredDeliveryMethods")}>
              <div className="w-[65%]">
                <Select
                  options={deliveryMethodOptions}
                  onChange={(val) => setSingleField("deliveryMethods", val)}
                  isMulti
                  value={fields.deliveryMethods}
                  getOptionLabel={(option) => `${tCommon(`${option.label}`)}`}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  menuPortalTarget={document.getElementById("root")}
                />
              </div>
            </Wrapper>

            <Wrapper title={t("desiredSources")}>
              <div className="w-[65%]">
                <Select
                  options={sourceOptions}
                  onChange={(val) => setSingleField("sources", val)}
                  isMulti
                  value={fields.sources}
                  getOptionLabel={(option) =>
                    `${tCommon(`${option.translationKey}`)}`
                  }
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  menuPortalTarget={document.getElementById("root")}
                />
              </div>
            </Wrapper>

            <Boolean
              onChange={(e) =>
                setSingleField("acceptedAutomatically", e.target.checked)
              }
              checked={fields.acceptedAutomatically}
            >
              {t("acceptOrderAuto")}
            </Boolean>

            <Boolean
              onChange={(e) =>
                setSingleField("isPosOrderAcceptedAuto", e.target.checked)
              }
              checked={fields.isPosOrderAcceptedAuto}
            >
              {t("posOrderAcceptedAuto")}
            </Boolean>
            {fields.isPosOrderAcceptedAuto && (
              <Boolean
                onChange={(e) =>
                  setSingleField("isPosOrderPrintedAuto", e.target.checked)
                }
                checked={fields.isPosOrderPrintedAuto}
              >
                {t("posOrderPrintedAuto")}
              </Boolean>
            )}

            <Boolean
              onChange={(e) => setSingleField("newOrders", e.target.checked)}
              checked={fields.newOrders}
            >
              {t("showOnlyNewOrders")}
            </Boolean>

            <Boolean
              onChange={(e) =>
                setSingleField(
                  "isOrderConfirmAsapMinButtonActive",
                  e.target.checked
                )
              }
              checked={fields.isOrderConfirmAsapMinButtonActive}
            >
              {t("minuteChoiceWithAsap")}
            </Boolean>

            <Boolean
              onChange={(e) =>
                setSingleField("stickyPosOrder", e.target.checked)
              }
              checked={fields.stickyPosOrder}
            >
              {t("stickyPosOrder")}
            </Boolean>
            <NativeNotificationEnabler />
            <Boolean
              onChange={(e) =>
                setSingleField("showOrderCount", e.target.checked)
              }
              checked={fields.showOrderCount}
            >
              {t("showOrderCount")}
            </Boolean>

            <Wrapper title={t("lanes")}>
              <div className="flex gap-4">
                {orderStatusses.map((statue) => {
                  const value = `${statue.value[0]}`;
                  return (
                    <Checkbox
                      active={fields.lanes.includes(value)}
                      checked={fields.lanes.includes(value)}
                      sizing="mini"
                      onChange={(e) => {
                        e.target.checked
                          ? setSingleField("lanes", [...fields.lanes, value])
                          : setSingleField(
                              "lanes",
                              fields.lanes.filter((l) => l !== value)
                            );
                      }}
                    >
                      {t(statue.translationKey)}
                    </Checkbox>
                  );
                })}
              </div>
            </Wrapper>

            <div className="flex justify-end">
              <Button type="submit" isPending={putUserSettings.isPending}>
                {t("save")}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

interface TrackerSettingProps {
  timeToNextDay: { label: string; value: string };
  isFoodTrackerActive: boolean;
  use_kitchen_screen: boolean;
  use_packaging_screen: boolean;
  use_formulas: boolean;
  icon_cloudinary_image_id: string;
  icon_cloudinary_image_file: any;
  receipt_branch_ids: number[];
}

const TrackerSettings = ({ onExit }) => {
  const { t } = useTranslation("", { keyPrefix: "orders_live" });
  const { t: tCommon } = useTranslation();
  const branchId = useBranchId();
  const { branches } = useLayoutData();
  const getCreateSignature = useOrderTrackerIconSignature();
  const getUpdatedSignature = useOrderTrackerIconSignatureUpdate();
  const { systemTrackerQuery, putTrackerSettings } = useSystemTrackerHook();
  const uploadToCloudinary = useUploadCloudinaryHook();
  const [fields, setFields] = useImmer<TrackerSettingProps>({
    timeToNextDay: { label: "00:00", value: "0" },
    isFoodTrackerActive: null,
    use_kitchen_screen: false,
    use_packaging_screen: false,
    use_formulas: false,
    icon_cloudinary_image_id: "",
    icon_cloudinary_image_file: null,
    receipt_branch_ids: [],
  });

  const getSignature = () => {
    // if (systemTrackerQuery.data?.icon_cloudinary_image_id) {
    //   return getUpdatedSignature.mutateAsync(
    //     systemTrackerQuery.data?.icon_cloudinary_image_id
    //   );
    // }
    return getCreateSignature.mutateAsync();
  };

  useEffect(() => {
    if (systemTrackerQuery.data) {
      setFields((draft) => {
        draft.isFoodTrackerActive =
          systemTrackerQuery.data.is_food_tracker_active;
        draft.timeToNextDay = timeToNextDayOptions.find(
          (i) => i.value === (systemTrackerQuery.data?.time_to_next_day ?? "0")
        );

        draft.use_kitchen_screen = systemTrackerQuery.data.use_kitchen_screen;
        draft.use_packaging_screen =
          systemTrackerQuery.data.use_packaging_screen;
        draft.use_formulas = systemTrackerQuery.data.use_formulas;
        draft.icon_cloudinary_image_id =
          systemTrackerQuery.data.icon_cloudinary_image_id;
        draft.receipt_branch_ids =
          systemTrackerQuery.data.receipt_branch_ids ?? [];
      });
    }
  }, [systemTrackerQuery.data]);

  const setSingleField = (
    desiredField: keyof TrackerSettingProps,
    value: any
  ) => {
    setFields((draft) => {
      //@ts-ignore
      draft[desiredField] = value;
    });
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    let submitData = {
      is_food_tracker_active: fields.isFoodTrackerActive,
      time_to_next_day: fields.timeToNextDay.value,
      use_packaging_screen: fields.use_packaging_screen,
      use_kitchen_screen: fields.use_kitchen_screen,
      use_formulas: fields.use_formulas,
      icon_cloudinary_image_id: fields.icon_cloudinary_image_id,
      receipt_branch_ids: fields.receipt_branch_ids,
    };

    if (fields.icon_cloudinary_image_file !== null) {
      return getSignature().then((data) => {
        const res = getFormData(fields.icon_cloudinary_image_file[0], data);
        uploadToCloudinary
          .mutateAsync({ upload_url: data.upload_url, res })
          .then(() => {
            submitData = {
              ...submitData,
              icon_cloudinary_image_id: data?.file_id,
            };
            putTrackerSettings.mutateAsync(submitData, {
              onSuccess: () => onExit(),
            });
          });
      });
    }
    return putTrackerSettings.mutateAsync(submitData, {
      onSuccess: () => onExit(),
    });
  };

  const branchesOptions = branches.filter(
    (branch) => branch.branch.id !== branchId
  );

  return (
    <div>
      {systemTrackerQuery.isPending && <Spinner />}
      {systemTrackerQuery.isError && <div>error</div>}
      {systemTrackerQuery.isSuccess && (
        <form onSubmit={onSubmitForm}>
          <div className="flex flex-col justify-between gap-8">
            <Wrapper title={t("timeToNextDay")}>
              <div className="w-50">
                <Select
                  options={timeToNextDayOptions}
                  onChange={(val) => setSingleField("timeToNextDay", val)}
                  value={fields.timeToNextDay}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </Wrapper>
            <Boolean
              onChange={(e) =>
                setSingleField("isFoodTrackerActive", e.target.checked)
              }
              checked={fields.isFoodTrackerActive}
            >
              {t("activateFoodTracker")}
            </Boolean>
            <Boolean
              onChange={(e) =>
                setSingleField("use_kitchen_screen", e.target.checked)
              }
              checked={fields.use_kitchen_screen}
            >
              {tCommon("navigation.kitchenScreens")}
            </Boolean>
            <Boolean
              onChange={(e) =>
                setSingleField("use_packaging_screen", e.target.checked)
              }
              checked={fields.use_packaging_screen}
            >
              {tCommon("packaging")}
            </Boolean>

            <Boolean
              onChange={(e) => setSingleField("use_formulas", e.target.checked)}
              checked={fields.use_formulas}
            >
              {tCommon("mergeBranches")}
            </Boolean>

            <Wrapper title={t("receiptBranchIds")}>
              <div className="w-[50%]">
                <Select
                  options={branchesOptions}
                  onChange={(branches) => {
                    const selectedBranchIds = branches.map((b) => b.branch.id);
                    setSingleField("receipt_branch_ids", selectedBranchIds);
                  }}
                  value={branchesOptions.filter((branch) =>
                    fields.receipt_branch_ids.includes(branch.branch.id)
                  )}
                  getOptionLabel={(option) => option.branch.name}
                  getOptionValue={(option) => option.branch.id}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  isMulti
                />
              </div>
            </Wrapper>

            <div>
              <File
                isDeletable
                onClickDelete={() =>
                  setFields((draft) => {
                    draft.icon_cloudinary_image_id = "";
                    draft.icon_cloudinary_image_file = null;
                  })
                }
                size="10rem"
                onChange={async (file) => {
                  setFields((draft) => {
                    draft.icon_cloudinary_image_file = file;
                  });
                }}
                src={getOrderTrackerIconUrl(
                  branchId,
                  fields.icon_cloudinary_image_id
                )}
              />
            </div>

            <div className="mt-60 flex justify-end">
              <Button type="submit" isPending={putTrackerSettings.isPending}>
                {t("save")}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const Wrapper = ({ children, title }: any) => {
  return (
    <div className="flex items-center justify-between gap-4">
      {title && <div className="text-smaller ml-3">{title}</div>}
      {children}
    </div>
  );
};

/* ------COMPONENTS-END */

export default SystemTrackerModal;
